import { useEffect, useState } from "react"
import * as Yup from 'yup'
import { useFormik } from 'formik'
import Select from "react-select"
import { toast } from "react-toastify"
import { boundaryCRUD } from "../../../actions/boundary"
import { dictionaryCRUD } from "../../../actions/dictionary"

const editSchema = Yup.object().shape({
  full_name: Yup.string().required('Full name is required'),
  phone_number: Yup.string().required('Phone number is required'),
  dob: Yup.string().nullable(),
  emaila_ddress: Yup.string().email('Wrong email format').nullable(),
})

/**
 * Combined View / Edit modal for a single boundary leader.
 * mode === 'view'  -> read-only
 * mode === 'edit'  -> editable + save (PUT /boundary/leader/<id>)
 *
 * `leader` is the row object from the list (already has all fields).
 */
export const ViewEditLeader = ({ leader, mode, setIndex, index }) => {

  const isView = mode === 'view'
  const [loading, setLoading] = useState(false)
  const [gender, setGender] = useState([])
  const [leaderType, setLeaderType] = useState([])

  // The leader-type currently chosen in the edit form (defaults to the row's).
  const [leaderTypeId, setLeaderTypeId] = useState(null)

  // Cascading dropdown states (Region -> District -> Shehia)
  const [regions, setRegions] = useState([])
  const [districts, setDistricts] = useState([])
  const [boundary, setBoundary] = useState([])
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [selectedDistrict, setSelectedDistrict] = useState(null)

  // 30 = Shehia/Locality leader, 31 = District leader, 32 = Region leader
  const showCascading = !isView && (leaderTypeId === 31 || leaderTypeId === 30)
  const showRegionOnly = !isView && leaderTypeId === 32

  useEffect(() => {
    dictionaryCRUD.loadDictionaryData(50, 0, '', 1).then((resp) => {
      setGender(resp.data.results)
    }).catch(() => {})
    dictionaryCRUD.loadDictionaryData(50, 0, '', 10).then((resp) => {
      setLeaderType(resp.data.results)
    }).catch(() => {})
  }, [])

  // Seed leaderTypeId from the selected row each time the modal opens.
  useEffect(() => {
    setLeaderTypeId(leader?.dictionary_leader_type || null)
    setSelectedRegion(null)
    setSelectedDistrict(null)
  }, [leader])

  // Load regions / boundary list when the chosen leader type changes (edit mode).
  useEffect(() => {
    if (isView) return
    setSelectedRegion(null)
    setSelectedDistrict(null)
    setDistricts([])
    setBoundary([])
    if (leaderTypeId === 32) {
      boundaryCRUD.loadBoundary(1000, 0, 'Region', '').then((resp) => {
        setBoundary(resp.data.results)
      }).catch(() => {})
    } else if (leaderTypeId === 31 || leaderTypeId === 30) {
      boundaryCRUD.loadBoundary(1000, 0, 'Region', '').then((resp) => {
        setRegions(resp.data.results)
      }).catch(() => {})
    }
  }, [leaderTypeId, isView])

  // Load districts when a region is selected.
  useEffect(() => {
    if (selectedRegion) {
      boundaryCRUD.loadBoundaryByParent(selectedRegion).then((resp) => {
        setDistricts(resp.data)
        if (leaderTypeId === 31) setBoundary(resp.data)
      }).catch(() => {})
    }
    setSelectedDistrict(null)
    if (leaderTypeId === 30) setBoundary([])
  }, [selectedRegion])

  // Load shehias when a district is selected (Shehia leader).
  useEffect(() => {
    if (selectedDistrict && leaderTypeId === 30) {
      boundaryCRUD.loadBoundaryByParent(selectedDistrict).then((resp) => {
        setBoundary(resp.data)
      }).catch(() => {})
    }
  }, [selectedDistrict])

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      full_name: leader?.full_name || '',
      phone_number: leader?.phone_number || '',
      dob: leader?.dob || '',
      emaila_ddress: leader?.emaila_ddress || '',
      dictionary_gender: leader?.dictionary_gender || '',
      dictionary_leader_type: leader?.dictionary_leader_type || '',
      boundary_id: leader?.boundary_id || '',
      is_active: leader?.is_active ?? true,
    },
    validationSchema: editSchema,
    onSubmit: (values, { setSubmitting }) => {
      setLoading(true)
      boundaryCRUD.updateBoundaryLeader(leader.id, values).then(() => {
        toast.success('Leader has been updated.', { autoClose: 7000 })
        setIndex(index + 1)
      }).catch((error) => {
        toast.error(error?.message || 'Update failed', { autoClose: 7000 })
      }).finally(() => {
        setLoading(false)
        setSubmitting(false)
      })
    }
  })

  const genderOptions = gender.map((dt) => ({ value: dt.id, label: dt.dictionary_item_name_en }))
  const typeOptions = leaderType.map((dt) => ({ value: dt.id, label: dt.dictionary_item_name_en }))

  return (
    <div className="modal fade viewEditLeaderModal" tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isView ? 'Leader Details' : 'Edit Leader'}</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <hr />
          <form onSubmit={formik.handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Full Name <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" {...formik.getFieldProps('full_name')} readOnly={isView} />
                  {formik.touched.full_name && formik.errors.full_name && (
                    <span className='text-danger' style={{ fontSize: '13px' }}>{formik.errors.full_name}</span>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Username</label>
                  <input type="text" className="form-control" value={leader?.username || 'N/A'} readOnly />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Phone Number <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" {...formik.getFieldProps('phone_number')} readOnly={isView} />
                  {formik.touched.phone_number && formik.errors.phone_number && (
                    <span className='text-danger' style={{ fontSize: '13px' }}>{formik.errors.phone_number}</span>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Email Address</label>
                  <input type="email" className="form-control" {...formik.getFieldProps('emaila_ddress')} readOnly={isView} />
                  {formik.touched.emaila_ddress && formik.errors.emaila_ddress && (
                    <span className='text-danger' style={{ fontSize: '13px' }}>{formik.errors.emaila_ddress}</span>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Date of Birth</label>
                  <input type="date" className="form-control" {...formik.getFieldProps('dob')} readOnly={isView} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Gender</label>
                  {isView ? (
                    <input type="text" className="form-control" value={leader?.gender || 'N/A'} readOnly />
                  ) : (
                    <Select
                      options={genderOptions}
                      value={genderOptions.find((o) => o.value === formik.values.dictionary_gender) || null}
                      placeholder="Select gender"
                      onChange={(e) => formik.setFieldValue('dictionary_gender', e?.value)}
                    />
                  )}
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Leader Type</label>
                  {isView ? (
                    <input type="text" className="form-control" value={leader?.leader_type || 'N/A'} readOnly />
                  ) : (
                    <Select
                      options={typeOptions}
                      value={typeOptions.find((o) => o.value === formik.values.dictionary_leader_type) || null}
                      placeholder="Select leader type"
                      onChange={(e) => {
                        formik.setFieldValue('dictionary_leader_type', e?.value)
                        setLeaderTypeId(e?.value)
                      }}
                    />
                  )}
                </div>
              </div>

              {/* View mode: show current boundary read-only.
                  Edit mode: cascading Region -> District -> Shehia. */}
              {isView ? (
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Boundary</label>
                    <input type="text" className="form-control" value={leader?.boundary_name || 'N/A'} readOnly />
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-12 mb-2">
                    <small className="text-muted">
                      Current: <strong>{leader?.boundary_name || 'N/A'}</strong>. Pick below to change it.
                    </small>
                  </div>
                  {(showCascading || showRegionOnly) && (
                    <div className={showRegionOnly ? "col-md-12 mb-3" : "col-md-4 mb-3"}>
                      <label className="form-label">{showRegionOnly ? 'Region' : 'Region'}</label>
                      {showRegionOnly ? (
                        <Select
                          options={boundary.map((d) => ({ value: d.id, label: d.boundary_name }))}
                          placeholder="Select region"
                          onChange={(e) => formik.setFieldValue('boundary_id', e?.value)}
                        />
                      ) : (
                        <Select
                          options={regions.map((d) => ({ value: d.id, label: d.boundary_name }))}
                          value={selectedRegion ? regions.filter(r => r.id === selectedRegion).map(r => ({ value: r.id, label: r.boundary_name }))[0] : null}
                          placeholder="Select region"
                          onChange={(e) => setSelectedRegion(e?.value)}
                        />
                      )}
                    </div>
                  )}
                  {leaderTypeId === 30 && (
                    <div className="col-md-4 mb-3">
                      <label className="form-label">District</label>
                      <Select
                        options={districts.map((d) => ({ value: d.id, label: d.boundary_name }))}
                        value={selectedDistrict ? districts.filter(d => d.id === selectedDistrict).map(d => ({ value: d.id, label: d.boundary_name }))[0] : null}
                        placeholder="Select district"
                        onChange={(e) => setSelectedDistrict(e?.value)}
                        isDisabled={!selectedRegion}
                      />
                    </div>
                  )}
                  {showCascading && (
                    <div className="col-md-4 mb-3">
                      <label className="form-label">{leaderTypeId === 31 ? 'District' : 'Shehia'}</label>
                      <Select
                        options={boundary.map((d) => ({ value: d.id, label: d.boundary_name }))}
                        placeholder={`Select ${leaderTypeId === 31 ? 'district' : 'shehia'}`}
                        onChange={(e) => formik.setFieldValue('boundary_id', e?.value)}
                        isDisabled={leaderTypeId === 31 ? !selectedRegion : !selectedDistrict}
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Status</label>
                  {isView ? (
                    <input type="text" className="form-control" value={leader?.is_active ? 'Active' : 'Not Active'} readOnly />
                  ) : (
                    <Select
                      options={[{ value: true, label: 'Active' }, { value: false, label: 'Not Active' }]}
                      value={{ value: formik.values.is_active, label: formik.values.is_active ? 'Active' : 'Not Active' }}
                      onChange={(e) => formik.setFieldValue('is_active', e?.value)}
                    />
                  )}
                </div>
              </div>
            </div>
            <hr />
            <div className="modal-footer">
              <a href="javascript:void(0);" className="btn btn-soft-danger fw-medium" data-bs-dismiss="modal">
                <i className="ri-close-line me-1 align-middle"></i> Close
              </a>
              {!isView && (
                <button type='submit' className="btn btn-soft-success" disabled={formik.isSubmitting}>
                  {!loading && <span><i className="las la-save la-lg"></i> Save</span>}
                  {loading && (
                    <span style={{ display: 'block' }}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
